import { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon, SparklesIcon } from '@heroicons/react/24/outline';
import type { RichEditorPreset } from '../types';

interface PresetMenuProps {
    presets: RichEditorPreset[];
    onPick: (preset: RichEditorPreset) => void;
}

/**
 * Right-aligned dropdown trigger that exposes available presets while the
 * editor already has content. Closes on outside-click and Escape so it
 * never traps focus. Pick handler closes the menu before delegating.
 */
export function PresetMenu({ presets, onPick }: PresetMenuProps) {
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    // Close on click outside / Escape so the menu doesn't trap focus.
    useEffect(() => {
        if (!open) return;
        const handleClick = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKey);
        };
    }, [open]);

    return (
        <div ref={wrapperRef} className="relative ml-auto">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={open}
                className={
                    'inline-flex h-7 items-center gap-1 rounded-md border px-2 text-xs font-medium transition-colors ' +
                    (open
                        ? 'border-gray-700 bg-gray-100 text-gray-900'
                        : 'border-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-900')
                }
            >
                <SparklesIcon className="h-3.5 w-3.5" />
                Presets
                <ChevronDownIcon
                    className={'h-3.5 w-3.5 transition-transform ' + (open ? 'rotate-180' : '')}
                />
            </button>

            {open && (
                <div
                    role="menu"
                    className="absolute right-0 top-full mt-1 z-30 w-72 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
                >
                    {presets.map((p) => (
                        <button
                            key={p.id}
                            type="button"
                            role="menuitem"
                            onClick={() => {
                                setOpen(false);
                                onPick(p);
                            }}
                            className="w-full text-left px-3 py-2.5 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                        >
                            <div className="text-sm font-semibold text-gray-800">{p.label}</div>
                            {p.description && (
                                <div className="text-xs text-gray-500 leading-snug mt-0.5">
                                    {p.description}
                                </div>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
